// #region imports

// --- Angular
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Inject } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, Subscription, combineLatest, distinctUntilChanged, map } from 'rxjs';
import { filter } from 'rxjs/operators';

// --- 3rd Party
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { Constants, GroupModel, InvitationTypes, LanguageModel, Respondent, RespondentEmailValidationModel, RespondentModel, SortDirections, SurveyResponseModel, SurveySummaryModel, UndoModel } from '../../../models';
import { FeaturesService, LanguagesBaseService, ManageRespondentsService, OidcService, ParticipantGroupsBaseService, RespondentReminderBaseService, RespondentsBaseService, ResponseBaseService, SurveyBaseService } from '../../../core/services';
import { environment } from '../../../../environments/environment';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LIB_VERSION as version } from '../../../../version';

// #endregion

@Component({
  selector: 'hs-manage-respondents-home-v2',
  templateUrl: './manage-respondents-home-v2.component.html',
  styleUrls: ['./manage-respondents-home-v2.component.scss']
})
export class ManageRespondentsHomeV2Component implements OnInit, OnDestroy {
  openPacksFeatureEnabled: boolean = false;
  appVersion: string = "1.0"/*version*/;

  groups$: Observable<GroupModel[]> | undefined;
  hasChanges$: Observable<boolean> | undefined;
  languages$: Observable<LanguageModel[]> | undefined;
  previousRespondent: RespondentModel | null = null;
  previousRespondents$: Observable<RespondentModel[]> | undefined;
  respondentValidationResult$: Observable<RespondentEmailValidationModel | null> | null = null;
  selectedSurvey$: Observable<SurveySummaryModel | null> | undefined;
  surveyResponse$: Observable<SurveyResponseModel | null> | undefined;

  respondents$: BehaviorSubject<RespondentModel[]>;

  isMobile$: Observable<boolean>;
  isMobileUser$: Observable<boolean | null> | undefined;

  activeGroup: GroupModel | null = null;
  activeRespondent: RespondentModel = {} as RespondentModel;
  canAddGroups: boolean = false;
  canSave: boolean = false;
  editMode: string = Constants.EDIT_MODE_NONE;
  respondentIndex: number = 0;
  viewMode: string;
  readonly viewModeManage = Constants.VIEW_MODE_MANAGE;
  readonly viewModeAdd = Constants.VIEW_MODE_ADD;
  readonly viewModeGroupAddEdit = Constants.VIEW_MODE_GROUP_ADD_EDIT;
  readonly viewModeInvitePrevious = Constants.VIEW_MODE_INVITE_PREVIOUS;

  undo: UndoModel | null = null;
  private _canAddGroupsSubscription: Subscription = Subscription.EMPTY;
  private _canSaveSubscription: Subscription = Subscription.EMPTY;
  private _featuresSubscription: Subscription = Subscription.EMPTY;
  private _groupsSubscription: Subscription = Subscription.EMPTY;
  private _participantSubscription: Subscription = Subscription.EMPTY;
  private _languageSubscription: Subscription = Subscription.EMPTY;
  private _undoSubscription: Subscription = Subscription.EMPTY;

  private _participantId: string | null = null;


  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _featuresService: FeaturesService,
    private _groupsService: ManageRespondentsService,
    private _languageService: LanguagesBaseService,
    private _oidcService: OidcService,
    private _respondentReminderService: RespondentReminderBaseService,
    private _respondentsService: RespondentsBaseService,
    private _responseService: ResponseBaseService,
    private _surveyService: SurveyBaseService,
    private _translateService: TranslateService,
    private _breakpointObserver: BreakpointObserver,
    ) {
    this.viewMode = this.viewModeManage;
    this.respondents$ = new BehaviorSubject<RespondentModel[]>(new Array<RespondentModel>());

    this.isMobile$ = this._breakpointObserver.observe([Constants.MOBILE_MEDIA_QUERY])
    .pipe(
      distinctUntilChanged(),
      map((state) => state.matches),
    );
  }

  ngOnInit(): void {
    document.body.classList.add('hide-intercom');
    this._enforceEnglishLtr();

    this._languageSubscription = this._languageService.selectedLanguage$
      .pipe(filter((language) => language !== null))
      .subscribe(() => {
        this._enforceEnglishLtr();
      });

    this._canAddGroupsSubscription = combineLatest({
      groups: this._groupsService.participantGroups$,
      surveyResponse: this._responseService.activeResponse$
    }).subscribe(({groups, surveyResponse}) => {
      if (!groups || !surveyResponse) return;

      this.canAddGroups = surveyResponse?.numberOfGroups > groups?.length;
    });

    this._canSaveSubscription = this._groupsService.hasChanges$.subscribe(hasChanges => {
      this.canSave = hasChanges;
    })
    this._featuresSubscription = this._featuresService.openPacksFeatureState$.subscribe((featureState) => {
      this.openPacksFeatureEnabled = featureState.toLowerCase() === Constants.FEATURE_ON.toLowerCase();
      if (this.openPacksFeatureEnabled) {
        const participantId = localStorage.getItem(Constants.PARTICIPANT_SELECTED_SURVEY);
        if (participantId) {
          this._surveyService.getSurveySummary(participantId);
          this._responseService.loadResponse(participantId);
          this._groupsService.refreshGroups(participantId);
          this._participantId = participantId;
        } else {
          this._surveyService.loadSummaries(1, 1, '', '', SortDirections.descending);
        }
      }
    });
    this._groupsSubscription = this._groupsService.participantGroups$.subscribe((groups) => {
      if (!groups) {
        return;
      }

      this.respondents$.next(_.flatten(groups.map((g) => g.respondents)));
    });
    this._participantSubscription = this._responseService.activeResponse$.subscribe(r => {
      if (r) this._participantId = r.id;
    });
    this._undoSubscription = this._groupsService.undo$.subscribe(undo => this.undo = undo);

    this.isMobileUser$ = this._oidcService.isMobileUser$;

    this.groups$ = this._groupsService.participantGroups$;
    this.hasChanges$ = this._groupsService.hasChanges$;
    this.languages$ = this._languageService.languages$;
    this.previousRespondents$ = this._respondentsService.previousRespondents$;
    this.respondentValidationResult$ = this._respondentsService.respondentValidationResult$;
    this.selectedSurvey$ = this._surveyService.selectedSelfSurvey$;
    this.surveyResponse$ = this._responseService.activeResponse$;

    this._groupsService.createSnapshot();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('hide-intercom');

    if (this._canAddGroupsSubscription !== Subscription.EMPTY) {
      this._canAddGroupsSubscription.unsubscribe();
      this._canAddGroupsSubscription = Subscription.EMPTY;
    }
    if (this._canSaveSubscription !== Subscription.EMPTY) {
      this._canSaveSubscription.unsubscribe();
      this._canSaveSubscription = Subscription.EMPTY;
    }
    if (this._featuresSubscription !== Subscription.EMPTY) {
      this._featuresSubscription.unsubscribe();
      this._featuresSubscription = Subscription.EMPTY;
    }
    if (this._groupsSubscription !== Subscription.EMPTY) {
      this._groupsSubscription.unsubscribe();
      this._groupsSubscription = Subscription.EMPTY;
    }
    if (this._participantSubscription !== Subscription.EMPTY) {
      this._participantSubscription.unsubscribe();
      this._participantSubscription = Subscription.EMPTY;
    }
    if (this._languageSubscription !== Subscription.EMPTY) {
      this._languageSubscription.unsubscribe();
      this._languageSubscription = Subscription.EMPTY;
    }
    if (this._undoSubscription !== Subscription.EMPTY) {
      this._undoSubscription.unsubscribe();
      this._undoSubscription = Subscription.EMPTY;
    }
  }

  onAddGroup(surveyResponse: SurveyResponseModel): void {
    this.activeGroup = this._groupsService.createGroup(surveyResponse.id, surveyResponse.numberOfRespondentsPerGroup);
    this.editMode = Constants.EDIT_MODE_ADD;
    this.viewMode = this.viewModeGroupAddEdit;
  }

  onAddGroupButton(): void {
    this._responseService.activeResponse$.subscribe(surveyResponse => {
      if (!surveyResponse) return;
      this.onAddGroup(surveyResponse);
    }).unsubscribe();
  }

  onAddPreviousClicked(respondent: RespondentModel) {
    this.previousRespondent = respondent;
  }

  onAddRespondent(addRespondentParams: any): void {
    const respondent = addRespondentParams['respondent'];
    const group = addRespondentParams['group'];
    const respondentIndex = addRespondentParams['respondentIndex'];

    this.activeRespondent = respondent;
    this.activeGroup = group;
    this.respondentIndex = respondentIndex;

    this.viewMode = this.viewModeAdd;
  }

  onBackButtonClicked(): void {
    this.viewMode = this.viewModeManage;

    this.activeRespondent = {} as RespondentModel;
    this.activeGroup = null;
    this.respondentIndex = 0;
  }

  onCancelClicked(): void {
    this._groupsService.clearSnapshot();
    localStorage.removeItem(Constants.PARTICIPANT_REDIRECT_DESTINATION_KEY);
    window.location.href = environment.mobileCallbackUrl;
  }

  onDeleteRespondent(respondent: RespondentModel) {
    // ToDo: Allow administrators to delete respondents who have started
    this._groupsService.deleteRespondent(respondent);
  }

  onDismissPreviousClicked(): void {
    this.previousRespondent = null;
    this.viewMode = this.viewModeAdd;
  }

  onDismissModal(reason: string): void {
    this._groupsService.clearSnapshot();
  }

  onDonePreviousClicked(): void {
    if (this.previousRespondent) {
      this.activeRespondent = this.previousRespondent;
      this.previousRespondent = null;
    };

    this.viewMode = this.viewModeAdd;
  }

  onEditGroup(group: GroupModel): void {
    this.activeGroup = group;
    this.editMode = Constants.EDIT_MODE_EDIT;
    this.viewMode = this.viewModeGroupAddEdit;
  }

  onGroupAdded(group: GroupModel): void {
    if (this.activeGroup === null) {
      this.viewMode = this.viewModeManage;
      return;
    }

    this._groupsService.addGroup(group);

    this.activeGroup = null;
    this.viewMode = this.viewModeManage;
  }

  onGroupDeleted(group: GroupModel) {
    this._groupsService.deleteGroup(group);
    this.viewMode = this.viewModeManage;
  }

  onGroupEdited(groupName: string): void {
    if (this.activeGroup === null) {
      this.viewMode = this.viewModeManage;
      return;
    }

    this.activeGroup.groupName = groupName;
    this._groupsService.updateGroup(this.activeGroup);

    this.activeGroup = null;
    this.viewMode = this.viewModeManage;
  }

  onInvitePreviousRespondent(): void {
    this.viewMode = this.viewModeInvitePrevious;
  }

  onMoveRespondent(moveRespondentParams: any): void {
    // ToDo: Allow administrators to move respondents who have started
    const respondent = moveRespondentParams['respondent'];
    const target = moveRespondentParams['targetGroup'];
    this._groupsService.moveRespondent(respondent, target);
  }

  onRemindRespondentClick(respondent: RespondentModel) {
    this._respondentReminderService.reminderRequest({
      recipientId: respondent.id ?? '',
      senderId: respondent.participantId ?? '',
      invitationType: InvitationTypes.respondentReminder,
    });
  }

  onSaveClicked() {
    if (!this._participantId) return;

    this._groupsService.saveChanges(this._participantId);
  }

  onSaveRespondent(respondent: RespondentModel) {
    this._groupsService.addRespondent(respondent);

    this.viewMode = this.viewModeManage;

    this.activeRespondent = {} as RespondentModel;
    this.activeGroup = null;
    this.respondentIndex = 0;
  }

  onUndo(): void {
    this._groupsService.undo();
  }

  onUndoDismissed(): void {
    this._groupsService.clearUndo();
  }

  onValidatingRespondentEmail(model: RespondentEmailValidationModel): void {
    this._groupsService.validateRespondentEmailAddress(model);
  }

  private _enforceEnglishLtr(): void {

    this._document.documentElement.lang = Constants.DEFAULT_LANGUAGE.isoTwoLetterName;
    this._document.documentElement.dir = 'ltr';
    this._translateService.use(Constants.DEFAULT_LOCALE);
  }
}
